import * as Yup from 'yup';
import {
  useCreateShipmentLineMutation,
  useGetStockLotsServerSideQuery,
} from 'store/slices/apiV1/inventory';
import { getCopy } from 'views/inventory/InventoryShipment/components/ShipmentAddStockLotDialog/copy';
import useCreateRecordDialog from 'hooks/useCreateRecordDialog';
import { createUpdateTeamFormConstants } from 'views/properties/Teams/components/constants';
import {
  HookFormInput,
  HookQuerySearchSelect,
} from 'ui-component/HookFormComponents';
import { packageValueOptions, StockLotQuerySchema } from 'types/inventory';
import _ from 'lodash';
import useGetCustomPropertyDefinitions from 'hooks/useGetCustomPropertyDefinitions';
import {
  CustomPropertyModel,
  CustomPropertyVisibility,
} from 'types/customProperty';
import { HookFormCustomPropertyInput } from 'ui-component/HookFormComponents/HookFormCustomPropertyInput';
import { Stack } from '@mui/material';
import { HookFormQuillInput } from 'ui-component/HookFormComponents/HookFormQuillInput';

const ShipmentAddStockLotDialog = ({
  open,
  onClose,
  shipmentId,
  excludedStockLotIds,
}: {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
  excludedStockLotIds: string[];
}) => {
  const copy = getCopy();

  const formConstants = {
    stockLotId: {
      id: 'stockLotId',
      label: 'Stock Lot',
    },
    notes: {
      id: 'notes',
      label: 'Shipping Line Notes',
    },
    printedNotes: {
      id: 'printedNotes',
      label: 'Stock Lot Label Notes',
    },
  };

  const {
    customProperties,
    getDefaultValuesForCustomProperties,
    customPropertiesFieldName,
  } = useGetCustomPropertyDefinitions({
    model: CustomPropertyModel.SHIPMENT_LINE,
    visibilityContext: CustomPropertyVisibility.NEW_RECORD_DIALOG,
  });

  const defaultValues = {
    [formConstants.stockLotId.id]: '',
    [formConstants.notes.id]: '',
    [formConstants.printedNotes.id]: '',
    [customPropertiesFieldName]: getDefaultValuesForCustomProperties(),
  };

  const validationSchema = Yup.object().shape({
    [formConstants.stockLotId.id]: Yup.string()
      .max(255, 'Stock Lot must be under 255 characters')
      .required('Stock Lot is required'),
    [formConstants.notes.id]: Yup.string().max(
      255,
      'Shipping Line Notes must be under 255 characters'
    ),
    [formConstants.printedNotes.id]: Yup.string().max(
      255,
      'Stock Lot Label Notes must be under 255 characters'
    ),
  });

  const { methods, control, errors, BaseCreateRecordDialog, dialogProps } =
    useCreateRecordDialog({
      formConstants: createUpdateTeamFormConstants,
      validationSchema,
      newRecordDefaults: defaultValues,
      useCreateMutation: useCreateShipmentLineMutation,
      overrideSuccessSnackbar: 'Stock Lot Added',
      overrideTitle: 'Add Stock Lot',
      submitAsPayload: true,
      otherProps: {
        shipmentId,
      },
      trackingNames: {
        close: copy.closeAddStockLotDialog,
        cancel: copy.cancelAddStockLot,
        save: copy.saveAddStockLot,
      },
    });

  return (
    <BaseCreateRecordDialog
      open={open}
      onClose={onClose}
      dialogProps={dialogProps}
    >
      <HookQuerySearchSelect<StockLotQuerySchema>
        errors={errors}
        control={control}
        name={formConstants.stockLotId.id}
        label={formConstants.stockLotId.label}
        useGetQuery={useGetStockLotsServerSideQuery}
        filters={[
          {
            field: 'quant',
            operator: '>',
            value: 0,
          },
        ]}
        excludes={[
          {
            field: 'id',
            operator: 'isAnyOf',
            value: excludedStockLotIds,
          },
        ]}
        schema={['quant', 'lotId', 'package', { part: ['mpn', 'id'] }]}
        getOptionLabel={(option) =>
          `${option.part?.mpn}: ${option.quant}pc ${
            option.package
              ? ` -  ${
                  _.find(packageValueOptions, ['value', option.package])?.label
                }`
              : ''
          } (${option.part?.id}${option.lotId})`
        }
      />
      <HookFormQuillInput
        fullWidth
        control={methods.control}
        name={formConstants.notes.id}
        label={formConstants.notes.label}
        errors={methods.formState.errors}
      />
      <HookFormInput
        fullWidth
        control={methods.control}
        name={formConstants.printedNotes.id}
        label={formConstants.printedNotes.label}
        errors={methods.formState.errors}
      />
      <Stack sx={{ mt: 2 }}>
        {_.map(customProperties, (property) => (
          <HookFormCustomPropertyInput
            control={control}
            errors={errors}
            key={property.key}
            customProperty={property}
            setValue={methods.setValue}
          />
        ))}
      </Stack>
    </BaseCreateRecordDialog>
  );
};

export default ShipmentAddStockLotDialog;
